import { useState, useEffect } from 'react';
import { collection, query, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { format, startOfDay, getDay, subMonths, subDays, differenceInMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface DreamFrequencyData {
  date: Date;
  count: number;
  serenityScore: number;
}

interface EmotionData {
  name: string;
  count: number;
  percentage: number;
}

interface KeywordData {
  keyword: string;
  count: number;
  percentage: number;
}

interface WeekdayData {
  day: string;
  serenityScore: number;
  count: number;
}

interface Approach {
  categoryId: string;
  categoryName: string;
  subcategoryId: string;
  subcategoryName: string;
}

interface Dream {
  id: string;
  createdAt: Date;
  emotions?: string;
  serenityScore?: number;
  approaches?: Approach[];
  keywords?: string[];
  analysisCount?: number;
}

interface UserSubscription {
  plan: 'FREE' | 'BASIC' | 'PREMIUM';
  startDate: Date;
}

interface MedalData {
  name: string;
  threshold: number;
  achieved: boolean;
  color: string;
}

interface ProgressData {
  dreamCount: {
    current: number;
    medals: MedalData[];
  };
  categoryCount: {
    current: number;
    medals: MedalData[];
  };
}

interface AnalysisData {
  name: string;
  count: number;
  percentage: number;
}

interface StatisticsData {
  dreamFrequencyData: DreamFrequencyData[];
  emotionData: EmotionData[];
  keywordsData: KeywordData[];
  weekdayData: WeekdayData[];
  progressData: ProgressData;
  analysesData: AnalysisData[];
  dreams: Dream[];  
}

const DREAM_MEDALS: MedalData[] = [
  { name: 'Premier Pas', threshold: 5, achieved: false, color: '#FF4B8C' },
  { name: 'Voyageur', threshold: 20, achieved: false, color: '#68f1d1' },
  { name: 'Explorateur', threshold: 100, achieved: false, color: '#556987' },
  { name: 'Maître', threshold: 350, achieved: false, color: '#9B6DFF' },
  { name: 'Légende', threshold: 1000, achieved: false, color: '#FFB86C' }
];

const CATEGORY_MEDALS: MedalData[] = [
  { name: 'Curieux', threshold: 10, achieved: false, color: '#FF4B8C' },
  { name: 'Aventurier', threshold: 15, achieved: false, color: '#68f1d1' }, 
  { name: 'Erudit', threshold: 6, achieved: false, color: '#556987' }, 
  { name: 'Sage', threshold: 12, achieved: false, color: '#9B6DFF' }, 
  { name: 'Oracle', threshold: 24, achieved: false, color: '#FFB86C' } 
];

export const useStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StatisticsData>({
    dreamFrequencyData: [],
    emotionData: [],
    keywordsData: [],
    weekdayData: [],
    progressData: {
      dreamCount: { current: 0, medals: [] },
      categoryCount: { current: 0, medals: [] }
    },
    analysesData: [],
    dreams: []  
  });

  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!currentUser) {
        setError('Utilisateur non connecté');
        setLoading(false);
        return;
      }

      try {
        // Récupérer l'abonnement de l'utilisateur
        const userRef = collection(db, 'users');
        const userDocRef = doc(userRef, currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (!userDoc.exists()) {
          throw new Error('Utilisateur non trouvé dans la base de données');
        }

        const userData = userDoc.data();
        // Convertir le plan en majuscules et s'assurer qu'il correspond à un des plans valides
        const planValue = (userData.plan || 'FREE').toUpperCase();
        const subscription: UserSubscription = {
          plan: (planValue === 'FREE' || planValue === 'BASIC' || planValue === 'PREMIUM') ? planValue : 'FREE',
          startDate: userData.subscriptionStartDate ? userData.subscriptionStartDate.toDate() : new Date()
        };

        // Récupérer tous les rêves de l'utilisateur
        const dreamsRef = collection(db, `users/${currentUser.uid}/dreams`);
        const dreamsSnapshot = await getDocs(dreamsRef);
        const dreams = dreamsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate()
        })) as Dream[];

        // Traitement des données de fréquence
        const frequencyMap = new Map<string, number>();
        const serenityMap = new Map<string, number>();
        const serenityCountMap = new Map<string, number>(); 

        dreams.forEach(dream => {
          const dateStr = format(startOfDay(dream.createdAt), 'yyyy-MM-dd');
          frequencyMap.set(dateStr, (frequencyMap.get(dateStr) || 0) + 1);
          
          if (typeof dream.serenityScore === 'number') {
            serenityMap.set(dateStr, (serenityMap.get(dateStr) || 0) + dream.serenityScore);
            serenityCountMap.set(dateStr, (serenityCountMap.get(dateStr) || 0) + 1);
          }
        });

        const frequencyData = Array.from(frequencyMap.entries()).map(([dateStr, count]) => {
          const totalSerenity = serenityMap.get(dateStr) || 0;
          const serenityCount = serenityCountMap.get(dateStr) || 0;
          return {
            date: new Date(dateStr),
            count,
            serenityScore: serenityCount > 0 ? totalSerenity / serenityCount : 0
          };
        }).sort((a, b) => a.date.getTime() - b.date.getTime());

        // Traitement des émotions
        const emotionsMap = new Map<string, number>();
        let totalEmotions = 0;
        
        dreams.forEach(dream => {
          if (dream.emotions) {
            const emotions = dream.emotions.split(',').map(e => e.trim());
            emotions.forEach(emotion => {
              emotionsMap.set(emotion, (emotionsMap.get(emotion) || 0) + 1);
              totalEmotions++;
            });
          }
        });

        const emotionsData = Array.from(emotionsMap.entries())
          .map(([emotion, count]) => ({
            name: emotion,
            count,
            percentage: (count / totalEmotions) * 100
          }))
          .sort((a, b) => b.count - a.count);

        // Traitement des mots-clés
        const keywordsCount = new Map<string, number>();
        dreams.forEach(dream => {
          if (dream.keywords) {
            dream.keywords.forEach(keyword => {
              keywordsCount.set(keyword, (keywordsCount.get(keyword) || 0) + 1);
            });
          }
        });

        const totalKeywords = Array.from(keywordsCount.values()).reduce((a, b) => a + b, 0);
        const keywordsDataTemp = Array.from(keywordsCount.entries())
          .map(([keyword, count]) => ({
            keyword,
            count,
            percentage: Math.round((count / totalKeywords) * 100)
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6);

        // Traitement des données par jour de la semaine
        const weekdayMap = new Map<number, { totalSerenity: number; count: number }>();
        dreams.forEach(dream => {
          if (typeof dream.serenityScore === 'number') {  
            const dayNumber = getDay(dream.createdAt);
            const currentDay = weekdayMap.get(dayNumber) || { totalSerenity: 0, count: 0 };
            weekdayMap.set(dayNumber, {
              totalSerenity: currentDay.totalSerenity + dream.serenityScore,
              count: currentDay.count + 1
            });
          }
        });

        const weekdays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const weekdayDataTemp = weekdays.map((day, index) => {
          const dayData = weekdayMap.get(index) || { totalSerenity: 0, count: 0 };
          return {
            day,
            serenityScore: dayData.count > 0 ? Math.round(dayData.totalSerenity / dayData.count) : 0,
            count: dayData.count
          };
        });

        console.log('Données par jour:', weekdayDataTemp); 

        // Traitement des analyses récurrentes
        const analysesMap = new Map<string, number>();
        let totalAnalyses = 0;
        
        dreams.forEach(dream => {
          if (dream.approaches && Array.isArray(dream.approaches)) {
            dream.approaches.forEach(approach => {
              if (approach.subcategoryName) {
                analysesMap.set(approach.subcategoryName, (analysesMap.get(approach.subcategoryName) || 0) + 1);
                totalAnalyses++;
              }
            });
          }
        });

        const analysesData = Array.from(analysesMap.entries())
          .map(([name, count]) => ({
            name,
            count,
            percentage: (count / totalAnalyses) * 100
          }))
          .sort((a, b) => b.count - a.count);

        // Traitement des données de progression
        const totalDreams = dreams.length;
        
        // Calcul des approches uniques une seule fois
        const uniqueApproaches = new Set(dreams.flatMap(dream => 
          dream.approaches?.map(a => a.subcategoryName) || []
        ));
        const totalApproaches = uniqueApproaches.size;
        console.log('Approches uniques trouvées:', Array.from(uniqueApproaches));

        const updatedDreamMedals = DREAM_MEDALS.map(medal => ({
          ...medal,
          achieved: totalDreams >= medal.threshold
        }));

        // Traitement des médailles de catégorie
        const categoryMedals = [...CATEGORY_MEDALS];
        
        // Curieux - Nombre d'approches
        categoryMedals[0].achieved = totalApproaches >= categoryMedals[0].threshold;

        // Aventurier - 15 analyses en 1 mois
        const lastMonth = subDays(new Date(), 30);
        const analysesLastMonth = dreams
          .filter(dream => {
            const createdAt = dream.createdAt instanceof Date ? dream.createdAt : new Date(dream.createdAt);
            return createdAt >= lastMonth;
          })
          .reduce((total, dream) => {
            return total + (dream.approaches?.length || 0);
          }, 0);
        
        console.log('Analyses sur les 30 derniers jours:', analysesLastMonth);
        categoryMedals[1].achieved = analysesLastMonth >= categoryMedals[1].threshold;

        // Erudit - 6 mois d'abonnement payant
        const subscriptionMonths = subscription.plan !== 'FREE'
          ? differenceInMonths(new Date(), subscription.startDate)
          : 0;
        console.log('Statut abonnement:', {
          plan: subscription.plan,
          startDate: subscription.startDate,
          moisAbonnement: subscriptionMonths
        });
        categoryMedals[2].achieved = subscriptionMonths >= categoryMedals[2].threshold && subscription.plan !== 'FREE';

        // Sage - 1 an d'abonnement payant
        categoryMedals[3].achieved = subscriptionMonths >= categoryMedals[3].threshold && subscription.plan !== 'FREE';

        // Oracle - 2 ans d'abonnement payant + 10 approches
        console.log('Conditions Oracle:', {
          moisAbonnement: subscriptionMonths,
          seuilMois: categoryMedals[4].threshold,
          nbApproches: totalApproaches,
          seuilApproches: 10,
          planPayant: subscription.plan !== 'FREE'
        });

        categoryMedals[4].achieved = subscriptionMonths >= categoryMedals[4].threshold && 
                                   subscription.plan !== 'FREE' && 
                                   totalApproaches >= 10;

        setData({
          dreamFrequencyData: frequencyData,
          emotionData: emotionsData,
          keywordsData: keywordsDataTemp,
          weekdayData: weekdayDataTemp,
          progressData: {
            dreamCount: {
              current: dreams.length,
              medals: updatedDreamMedals
            },
            categoryCount: {
              current: totalApproaches,
              medals: categoryMedals
            }
          },
          analysesData: analysesData,
          dreams: dreams
        });

        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des statistiques:', err);
        setError('Erreur lors de la récupération des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [currentUser]);

  return {
    loading,
    error,
    data
  };
};
